import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { X, Calendar, CheckCircle, Users, TrendingUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const AdolescentProfile = ({ adolescentId, open, onOpenChange }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && adolescentId) {
      fetchProfile();
    }
  }, [open, adolescentId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/adolescents/${adolescentId}/profile`);
      setProfile(response.data);
    } catch (error) {
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  if (!profile && !loading) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="adolescent-profile-dialog">
        <DialogHeader>
          <DialogTitle>Perfil Completo</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-12">Carregando...</div>
        ) : profile ? (
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
              <div className="flex items-center space-x-4">
                {profile.adolescent.photo_url ? (
                  <img src={profile.adolescent.photo_url} alt={profile.adolescent.name} className="avatar-large" />
                ) : (
                  <div className="avatar-large bg-gradient-to-br from-blue-200 to-purple-200 flex items-center justify-center text-blue-700 font-bold text-3xl">
                    {profile.adolescent.name.charAt(0)}
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>{profile.adolescent.name}</h2>
                  <p className="text-gray-600">{profile.adolescent.age} anos • {profile.adolescent.gender === 'masculino' ? 'Masculino' : 'Feminino'}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    <strong>Telefone:</strong> {profile.adolescent.phone || '-'}
                  </p>
                  {profile.current_small_group && (
                    <div className="mt-2 space-y-1">
                      <span className="badge badge-membro">{profile.current_small_group.name}</span>
                      {profile.current_small_group.leaders && profile.current_small_group.leaders.length > 0 && (
                        <p className="text-sm text-purple-700">
                          <strong>Líder(es):</strong> {profile.current_small_group.leaders.map(l => l.name).join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Guardian Info */}
              <div className="mt-4 pt-4 border-t border-blue-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">
                    <strong>Responsável 1:</strong> {profile.adolescent.guardian_name || '-'}
                    {profile.adolescent.guardian_relationship && (
                      <span className="text-gray-500"> ({profile.adolescent.guardian_relationship})</span>
                    )}
                  </p>
                  <p className="text-sm text-gray-500">
                    <strong>Telefone:</strong> {profile.adolescent.guardian_phone || '-'}
                  </p>
                </div>
                {profile.adolescent.guardian2_name && (
                  <div>
                    <p className="text-sm text-gray-600">
                      <strong>Responsável 2:</strong> {profile.adolescent.guardian2_name}
                    </p>
                    <p className="text-sm text-gray-500">
                      <strong>Telefone:</strong> {profile.adolescent.guardian2_phone || '-'}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Notes */}
              {profile.adolescent.notes && (
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <p className="text-sm text-gray-600">
                    <strong>Observações:</strong>
                  </p>
                  <p className="text-sm text-gray-700 mt-1 bg-white/50 p-2 rounded">
                    {profile.adolescent.notes}
                  </p>
                </div>
              )}
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="stat-card">
                <div className="stat-icon">
                  <CheckCircle size={24} />
                </div>
                <div className="stat-value">{profile.statistics.total_checkins}</div>
                <div className="stat-label">Check-ins em Cultos</div>
              </div>

              <div className="stat-card">
                <div className="stat-icon" style={{background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', color: '#065f46'}}>
                  <Users size={24} />
                </div>
                <div className="stat-value">{profile.statistics.total_pg_attendance}</div>
                <div className="stat-label">Presenças em PG</div>
              </div>

              <div className="stat-card">
                <div className="stat-icon" style={{background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', color: '#92400e'}}>
                  <TrendingUp size={24} />
                </div>
                <div className="stat-value">{profile.statistics.pg_attendance_rate}%</div>
                <div className="stat-label">Taxa de Presença PG</div>
              </div>
            </div>

            {/* History Tabs */}
            <Tabs defaultValue="cultos">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="cultos">Histórico de Cultos</TabsTrigger>
                <TabsTrigger value="pgs">Histórico de PG</TabsTrigger>
              </TabsList>

              <TabsContent value="cultos">
                <div className="card">
                  <h3 className="text-lg font-bold mb-4">Check-ins em Cultos</h3>
                  {profile.checkins_history && profile.checkins_history.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {profile.checkins_history.map((checkin) => (
                        <div key={checkin.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid="checkin-history-item">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                              <Calendar size={20} className="text-blue-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{checkin.event_name}</p>
                              <p className="text-sm text-gray-500">
                                {format(parseISO(checkin.checkin_time), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                              </p>
                            </div>
                          </div>
                          <span className={`badge badge-${checkin.status}`}>
                            {checkin.status === 'checked_in' ? 'Presente' : 'Saiu'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-gray-500">Nenhum check-in registrado</p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="pgs">
                <div className="card">
                  <h3 className="text-lg font-bold mb-4">Participação em Pequenos Grupos</h3>
                  {profile.pg_attendance_history && profile.pg_attendance_history.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {profile.pg_attendance_history.map((attendance) => (
                        <div key={attendance.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg" data-testid="pg-history-item">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                              <Users size={20} className="text-green-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{attendance.small_group_name}</p>
                              <p className="text-sm text-gray-500">
                                {format(parseISO(attendance.meeting_date), "dd/MM/yyyy", { locale: ptBR })}
                              </p>
                            </div>
                          </div>
                          <span className="badge badge-membro">Presente</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-gray-500">Nenhuma presença em PG registrada</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default AdolescentProfile;
